import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { addressesAPI } from '../api/vendly';
import styles from './CheckoutPage.module.css';

const formatPrice = (n) => `₦${Number(n).toLocaleString()}`;

const NIGERIAN_STATES = ['Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno','Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba','Yobe','Zamfara'];

function getDeliveryDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Delivery fee: flat ₦1,000 per unique seller (or just flat ₦1,000 per order)
const DELIVERY_FEE = 1000;

export default function CheckoutPage() {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]               = useState(1);
  const [payMethod, setPayMethod]     = useState('transfer');
  const [savedAddrs, setSavedAddrs]   = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null); // id of selected saved address
  const [useNew, setUseNew]           = useState(false);
  const [saveThis, setSaveThis]       = useState(false);
  const [address, setAddress]         = useState({
    name: user?.name || '', phone: '', line: '', city: '', state: '',
  });
  const [error, setError] = useState('');

  // Fixed delivery fee per order regardless of quantity
  const deliveryFee  = DELIVERY_FEE;
  const grandTotal   = total + deliveryFee;
  const deliveryDate = getDeliveryDate();

  useEffect(() => {
    addressesAPI.list()
      .then(res => {
        const addrs = res.data.addresses || [];
        setSavedAddrs(addrs);
        // Auto-select default
        const def = addrs.find(a => a.isDefault) || addrs[0];
        if (def) { setSelectedAddr(def.id); setUseNew(false); }
        else { setUseNew(true); }
      })
      .catch(() => setUseNew(true));
  }, []);

  const activeAddress = useNew
    ? address
    : savedAddrs.find(a => a.id === selectedAddr) || address;

  const handleAddress = (e) => setAddress(a => ({ ...a, [e.target.name]: e.target.value }));

  const handleConfirm = async () => {
    const addr = activeAddress;
    if (!addr.name || !addr.phone || !addr.line || !addr.city || !addr.state) {
      setError('Please fill in all address fields.');
      setStep(1);
      return;
    }

    // Save address if checkbox ticked
    if (useNew && saveThis) {
      try { await addressesAPI.save({ ...address, label: 'Home' }); } catch {}
    }

    clear();
    navigate('/checkout/processing', {
      state: {
        cartItems:   items.map(i => ({ id: i.id, name: i.name, qty: i.qty, price: i.price, image: i.image })),
        payMethod,
        deliveryDate,
        address: addr,
      }
    });
  };

  if (items.length === 0) return (
    <div className={styles.empty}>
      <span>🛒</span>
      <p>Your cart is empty</p>
      <Link to="/shop" className={styles.shopBtn}>Back to shop</Link>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <Link to="/cart" className={styles.backBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
          Back to cart
        </Link>
        <h1 className={styles.headerTitle}>Place your order</h1>
        <div className={styles.headerTrust}>
          <span>🔒 Secure checkout</span>
          <span>↩️ Easy returns</span>
          <span>✅ Verified sellers</span>
        </div>
      </div>

      <div className={`container ${styles.layout}`}>
        {/* Left — steps */}
        <div className={styles.steps}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* Step 1 — Address */}
          <div className={`${styles.stepCard} ${step === 1 ? styles.stepActive : styles.stepDone}`}>
            <div className={styles.stepHead} onClick={() => setStep(1)}>
              <div className={styles.stepNum}>
                {step > 1 ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : '1'}
              </div>
              <h2 className={styles.stepTitle}>Customer address</h2>
              {step > 1 && <span className={styles.changeBtn}>Change</span>}
            </div>

            {step === 1 && (
              <div className={styles.stepBody}>

                {/* Saved addresses */}
                {savedAddrs.length > 0 && (
                  <div className={styles.savedAddrs}>
                    <p className={styles.savedLabel}>Saved addresses</p>
                    <div className={styles.addrCards}>
                      {savedAddrs.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          className={`${styles.addrCard} ${selectedAddr === a.id && !useNew ? styles.addrCardActive : ''}`}
                          onClick={() => { setSelectedAddr(a.id); setUseNew(false); }}
                        >
                          <div className={styles.addrCardTop}>
                            <span className={styles.addrLabel}>{a.label}</span>
                            {a.isDefault && <span className={styles.addrDefault}>Default</span>}
                          </div>
                          <p className={styles.addrName}>{a.name}</p>
                          <p className={styles.addrLine}>{a.line}, {a.city}, {a.state}</p>
                          <p className={styles.addrPhone}>{a.phone}</p>
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`${styles.addrCard} ${styles.addrCardNew} ${useNew ? styles.addrCardActive : ''}`}
                        onClick={() => setUseNew(true)}
                      >
                        <span className={styles.addrPlusIcon}>+</span>
                        <p className={styles.addrNewText}>New address</p>
                      </button>
                    </div>
                  </div>
                )}

                {/* New address form */}
                {(useNew || savedAddrs.length === 0) && (
                  <>
                    <div className={styles.formRow}>
                      <div className={styles.field}>
                        <label className={styles.label}>Full name</label>
                        <input className={styles.input} name="name" value={address.name} onChange={handleAddress} placeholder="John Doe" />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Phone number</label>
                        <input className={styles.input} name="phone" value={address.phone} onChange={handleAddress} placeholder="08012345678" />
                      </div>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>Street address</label>
                      <input className={styles.input} name="line" value={address.line} onChange={handleAddress} placeholder="12 Allen Avenue, Ikeja" />
                    </div>
                    <div className={styles.formRow}>
                      <div className={styles.field}>
                        <label className={styles.label}>City / Area</label>
                        <input className={styles.input} name="city" value={address.city} onChange={handleAddress} placeholder="Ikeja" />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>State</label>
                        <select className={styles.input} name="state" value={address.state} onChange={handleAddress}>
                          <option value="">Select state</option>
                          {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <label className={styles.saveCheck}>
                      <input type="checkbox" checked={saveThis} onChange={e => setSaveThis(e.target.checked)} />
                      Save this address for future orders
                    </label>
                  </>
                )}

                <button className={styles.nextBtn} onClick={() => {
                  const addr = activeAddress;
                  if (!addr.name || !addr.phone || !addr.line || !addr.city || !addr.state) {
                    setError('Please fill in all address fields.');
                    return;
                  }
                  setError('');
                  setStep(2);
                }}>
                  Continue to delivery →
                </button>
              </div>
            )}

            {step > 1 && (
              <div className={styles.stepSummary}>
                <p className={styles.summaryText}>{activeAddress.name} · {activeAddress.phone}</p>
                <p className={styles.summaryText}>{activeAddress.line}, {activeAddress.city}, {activeAddress.state}</p>
              </div>
            )}
          </div>

          {/* Step 2 — Delivery */}
          <div className={`${styles.stepCard} ${step === 2 ? styles.stepActive : step > 2 ? styles.stepDone : styles.stepLocked}`}>
            <div className={styles.stepHead} onClick={() => step >= 2 && setStep(2)}>
              <div className={styles.stepNum}>
                {step > 2 ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : '2'}
              </div>
              <h2 className={styles.stepTitle}>Delivery details</h2>
              {step > 2 && <span className={styles.changeBtn}>Change</span>}
            </div>

            {step === 2 && (
              <div className={styles.stepBody}>
                <div className={styles.deliveryOption}>
                  <div className={styles.deliveryOptionLeft}>
                    <span className={styles.deliveryIcon}>🏠</span>
                    <div>
                      <p className={styles.deliveryName}>Home delivery</p>
                      <p className={styles.deliveryDesc}>Delivered to your address</p>
                    </div>
                  </div>
                  <div className={styles.deliveryRight}>
                    <p className={styles.deliveryFee}>{formatPrice(deliveryFee)}</p>
                    <div className={styles.radioActive} />
                  </div>
                </div>

                <div className={styles.deliveryDateBox}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <div>
                    <p className={styles.deliveryDateLabel}>Estimated delivery</p>
                    <p className={styles.deliveryDateValue}>{deliveryDate}</p>
                  </div>
                </div>

                <div className={styles.shipments}>
                  {items.map((item, i) => (
                    <div key={item.id} className={styles.shipmentItem}>
                      <img src={item.image} alt={item.name} className={styles.shipImg} />
                      <div className={styles.shipInfo}>
                        <p className={styles.shipName}>{item.name}</p>
                        <p className={styles.shipMeta}>x{item.qty} · {formatPrice(item.price)}</p>
                      </div>
                      <p className={styles.shipFee}>+{formatPrice(1000 * item.qty)} delivery</p>
                    </div>
                  ))}
                </div>

                <button className={styles.nextBtn} onClick={() => setStep(3)}>
                  Continue to payment →
                </button>
              </div>
            )}

            {step > 2 && (
              <div className={styles.stepSummary}>
                <p className={styles.summaryText}>🏠 Home delivery · {formatPrice(deliveryFee)} · By {deliveryDate}</p>
              </div>
            )}
          </div>

          {/* Step 3 — Payment */}
          <div className={`${styles.stepCard} ${step === 3 ? styles.stepActive : step > 3 ? styles.stepDone : styles.stepLocked}`}>
            <div className={styles.stepHead}>
              <div className={styles.stepNum}>3</div>
              <h2 className={styles.stepTitle}>Payment method</h2>
            </div>

            {step === 3 && (
              <div className={styles.stepBody}>
                {[
                  { id: 'transfer', label: 'Bank transfer', icon: '🏦', desc: 'Transfer directly to our account' },
                  { id: 'card', label: 'Pay with card', icon: '💳', desc: 'Visa, Mastercard, Verve' },
                  { id: 'delivery', label: 'Pay on delivery', icon: '🚫', desc: 'Not available at this time', disabled: true },
                ].map(m => (
                  <button
                    key={m.id}
                    className={`${styles.payOption} ${payMethod === m.id ? styles.payOptionActive : ''} ${m.disabled ? styles.payOptionDisabled : ''}`}
                    onClick={() => !m.disabled && setPayMethod(m.id)}
                  >
                    <span className={styles.payIcon}>{m.icon}</span>
                    <div className={styles.payInfo}>
                      <p className={styles.payLabel}>
                        {m.label}
                        {m.disabled && <span className={styles.slashBadge}>Unavailable</span>}
                      </p>
                      <p className={styles.payDesc}>{m.desc}</p>
                    </div>
                    <div className={`${styles.radio} ${payMethod === m.id ? styles.radioChecked : ''}`} />
                  </button>
                ))}

                <button
                  className={styles.confirmBtn}
                  onClick={handleConfirm}
                >
                  {`Confirm order · ${formatPrice(grandTotal)}`}
                </button>
                <p className={styles.termsNote}>
                  By confirming you agree to our <a href="#">Terms & Conditions</a>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right — order summary */}
        <div className={styles.summary}>
          <h3 className={styles.summaryTitle}>Order summary</h3>
          <div className={styles.summaryItems}>
            {items.map(item => (
              <div key={item.id} className={styles.summaryItem}>
                <img src={item.image} alt={item.name} className={styles.summaryImg} />
                <div className={styles.summaryItemInfo}>
                  <p className={styles.summaryItemName}>{item.name}</p>
                  <p className={styles.summaryItemQty}>x{item.qty}</p>
                </div>
                <p className={styles.summaryItemPrice}>{formatPrice(item.price * item.qty)}</p>
              </div>
            ))}
          </div>
          <div className={styles.summaryTotals}>
            <div className={styles.summaryRow}><span>Items ({items.reduce((s,i) => s+i.qty, 0)})</span><span>{formatPrice(total)}</span></div>
            <div className={styles.summaryRow}><span>Delivery</span><span>{formatPrice(deliveryFee)}</span></div>
            <div className={`${styles.summaryRow} ${styles.summaryGrand}`}><span>Total</span><span>{formatPrice(grandTotal)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
